import React, { useEffect, useMemo, useState } from 'react';
import {
  Container, Card, CardHeader, CardContent, Avatar,
  Grid, Typography, Button, TextField, Box, Snackbar, Alert
} from '@mui/material';
import Layout from '../Layout';
import api from '../api';

export default function ProfilePage() {
  const [user, setUser] = useState(null);

  // flow step: view -> otp -> reset
  const [step, setStep] = useState('view');

  const [otpValue, setOtpValue] = useState('');

  // OTP UX (mirrors backend: OTP TTL 10m, resend cooldown recommended 60s)
  const [otpExpiresAt, setOtpExpiresAt] = useState(null);
  const [otpRemainingMs, setOtpRemainingMs] = useState(0);

  const RESEND_COOLDOWN_MS = 60 * 1000; // should mirror backend cooldown policy
  const [resendRemainingMs, setResendRemainingMs] = useState(0);

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

  const [resetToken, setResetToken] = useState(undefined);

  const showToast = (message, severity = 'info') =>
    setToast({ open: true, message, severity });

  /* ─────────────────────────────────────────────────────────────
     FETCH USER + COMPANY DATA
  ────────────────────────────────────────────────────────────── */
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('detail/userinfo?view=profile');
        setUser(data);
      } catch (err) {
        console.error(err);
        showToast('Failed to load profile', 'error');
      }
    })();
  }, []);

  /* ─────────────────────────────────────────────────────────────
     OTP TIMER TICKS (expiry countdown + resend cooldown)
  ────────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (step !== 'otp') return;

    // initialize immediately on entry
    if (otpExpiresAt) {
      const ms = otpExpiresAt.getTime() - Date.now();
      setOtpRemainingMs(Math.max(0, ms));
    }

    const id = setInterval(() => {
      if (otpExpiresAt) {
        const ms = otpExpiresAt.getTime() - Date.now();
        setOtpRemainingMs(Math.max(0, ms));
      }
      setResendRemainingMs((prev) => Math.max(0, prev - 1000));
    }, 1000);

    return () => clearInterval(id);
  }, [step, otpExpiresAt]);

  const formatMs = (ms) => {
    const totalSec = Math.ceil(ms / 1000);
    const m = String(Math.floor(totalSec / 60)).padStart(2, '0');
    const s = String(totalSec % 60).padStart(2, '0');
    return `${m}:${s}`;
  };

  const otpExpired = useMemo(() => {
    if (!otpExpiresAt) return false;
    return otpRemainingMs <= 0;
  }, [otpExpiresAt, otpRemainingMs]);

  /* ─────────────────────────────────────────────────────────────
     STEP 1  → SEND OTP (also used for resend)
     Backend returns: { expiresAt }
  ────────────────────────────────────────────────────────────── */
  const handleSendOtp = async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/auth/send-otp');

      showToast('OTP sent to your email', 'success');

      // Align UI with server-provided expiry
      const exp = data?.expiresAt ? new Date(data.expiresAt) : null;
      setOtpExpiresAt(exp);
      if (exp) setOtpRemainingMs(Math.max(0, exp.getTime() - Date.now()));

      // Start UX cooldown (server should enforce too)
      setResendRemainingMs(RESEND_COOLDOWN_MS);

      setStep('otp');
      setOtpValue('');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to send OTP', 'error');
    } finally {
      setLoading(false);
    }
  };

  /* ─────────────────────────────────────────────────────────────
     STEP 2  → VERIFY OTP
  ────────────────────────────────────────────────────────────── */
  const handleVerifyOtp = async () => {
    const code = (otpValue || "").replace(/\D/g, "").slice(0, 6);

    if (code.length !== 6) {
      showToast("Enter the full 6-digit code", "warning");
      return;
    }

    if (otpExpired) {
      showToast("OTP has expired. Please resend a new code.", "warning");
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { otp: code });
      const token = (data?.resetToken || "").trim();
      if (!token) {
        showToast("OTP verification failed.", "error");
        setStep("otp");
        return;
      }

      showToast("OTP verified! You can now reset password.", "success");

      // store token + advance flow
      setResetToken(token);
      setStep("reset");

      // clear OTP UI state
      setOtpValue("");
      setOtpExpiresAt(null);
      setOtpRemainingMs(0);
      setResendRemainingMs(0);
    } catch (err) {
      showToast(err.response?.data?.message || "Invalid or expired OTP", "error");
      setStep("otp");
    } finally {
      setLoading(false);
    }
  };


  /* ─────────────────────────────────────────────────────────────
     STEP 3  → CHANGE PASSWORD
  ────────────────────────────────────────────────────────────── */
  const handleChangePassword = async () => {
    if (!resetToken) {
      showToast('Reset session expired. Please request OTP again.', 'warning');
      setStep('view');
      return;
    }
    if (newPassword !== confirmPassword) {
      showToast('Passwords do not match', 'warning');
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/change-password', {
        oldPassword: '', // ignored when OTP flow is active
        password: newPassword,
        resetToken
      });

      showToast('Password changed successfully!', 'success');

      // reset flow state
      setStep('view');
      setResetToken(undefined);
      setOtpValue('');
      setOtpExpiresAt(null);
      setOtpRemainingMs(0);
      setResendRemainingMs(0);
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to change password', 'error');
    } finally {
      setLoading(false);
    }
  };

  /* ─────────────────────────────────────────────────────────────
     RENDER
  ────────────────────────────────────────────────────────────── */
  if (!user) {
    return <Typography align="center" sx={{ mt: 4 }}>Loading profile…</Typography>;
  }

  const { companyDetails } = user;
  const companyName = companyDetails?.company_name || '';
  const flatLocations = (companyDetails?.locations || []).flat().join(', ');

  return (
    <Layout title="Profile">
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Card>
          <CardHeader
            avatar={<Avatar>{user?.name?.charAt(0) || 'U'}</Avatar>}
            title={user?.name || ''}
            subheader={user?.role || ''}
          />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="subtitle2">Email</Typography>
                <Typography>{user.email}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2">Phone</Typography>
                <Typography>{user.phone}</Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2">Company</Typography>
                <Typography>{companyName}</Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2">Locations</Typography>
                <Typography>{flatLocations}</Typography>
              </Grid>
            </Grid>

            {/* ───────── Change-Password flow ───────── */}
            <Box sx={{ mt: 4 }}>
              {step === 'view' && (
                <Button
                  variant="contained"
                  onClick={handleSendOtp}
                  disabled={loading}
                >
                  Change Password
                </Button>
              )}

              {step === 'otp' && (
                <Box>
                  <Typography>Enter the 6-digit code we just emailed you</Typography>

                  <TextField
                    label="OTP Code"
                    value={otpValue}
                    onChange={(e) =>
                      setOtpValue(e.target.value.replace(/\D/g, '').slice(0, 6))
                    }
                    fullWidth
                    sx={{ mt: 2 }}
                    inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                    disabled={loading}
                  />

                  {otpExpiresAt && (
                    <Typography sx={{ mt: 1 }} variant="body2">
                      {otpRemainingMs > 0
                        ? `Code expires in ${formatMs(otpRemainingMs)}`
                        : 'Code expired. Please resend a new code.'}
                    </Typography>
                  )}

                  <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                    <Button
                      variant="contained"
                      onClick={handleVerifyOtp}
                      disabled={loading || otpExpired || otpValue.length !== 6}
                    >
                      Verify Code
                    </Button>

                    <Button
                      variant="outlined"
                      onClick={handleSendOtp}
                      disabled={loading || resendRemainingMs > 0}
                    >
                      {resendRemainingMs > 0
                        ? `Resend in ${formatMs(resendRemainingMs)}`
                        : 'Resend Code'}
                    </Button>
                  </Box>

                  <Button
                    variant="text"
                    onClick={() => {
                      // allow user to exit OTP step
                      setStep('view');
                      setOtpValue('');
                      setOtpExpiresAt(null);
                      setOtpRemainingMs(0);
                      setResendRemainingMs(0);
                    }}
                    disabled={loading}
                    sx={{ mt: 1 }}
                  >
                    Cancel
                  </Button>
                </Box>
              )}

              {step === 'reset' && (
                <Box>
                  <TextField
                    label="New Password"
                    type="password"
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    fullWidth
                    sx={{ mt: 2 }}
                    disabled={loading}
                  />
                  <TextField
                    label="Confirm Password"
                    type="password"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    fullWidth
                    sx={{ mt: 2 }}
                    disabled={loading}
                  />
                  <Button
                    variant="contained"
                    onClick={handleChangePassword}
                    disabled={loading}
                    sx={{ mt: 2 }}
                  >
                    Submit
                  </Button>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Snackbar for toasts */}
        <Snackbar
          open={toast.open}
          autoHideDuration={4000}
          onClose={() => setToast(t => ({ ...t, open: false }))}
        >
          <Alert
            onClose={() => setToast(t => ({ ...t, open: false }))}
            severity={toast.severity}
            sx={{ width: '100%' }}
          >
            {toast.message}
          </Alert>
        </Snackbar>
      </Container>
    </Layout>
  );
}
